// src/utils/debugControl.js

const ORIGINAL_CONSOLE = {
  log: console.log,
  info: console.info,
  warn: console.warn,
  error: console.error
};

const IS_PRODUCTION = process.env.NODE_ENV === 'production';

class DebugControl {
  constructor() {
    // In production: always disabled, no browser control
    if (IS_PRODUCTION) {
      this.enabled = false;
      this.allowBrowserControl = false;
      console.log('🔇 Production mode: Debug logs permanently disabled');
    } else {
      // In development: controlled by env var, allow browser control
      this.enabled = process.env.REACT_APP_DEBUG !== 'false';
      this.allowBrowserControl = true;
      console.log('🔧 Development mode: Debug logs', this.enabled ? 'enabled' : 'disabled');
      console.log('💡 Use debugControl.enable()/disable()/toggle() to control logs');
    }

    this.setupOverride();
  }

  setupOverride() {
    if (!this.enabled) {
      // Disable console.log and console.info
      console.log = () => {};
      console.info = () => {};

      if (IS_PRODUCTION) {
        // In production, also disable console.warn
        console.warn = () => {};
      } else {
        // In development, keep console.warn
        console.warn = (...args) => ORIGINAL_CONSOLE.warn(...args);
      }

      // Always keep console.error
      console.error = (...args) => ORIGINAL_CONSOLE.error(...args);
    } else {
      // Restore all console methods
      console.log = ORIGINAL_CONSOLE.log;
      console.info = ORIGINAL_CONSOLE.info;
      console.warn = ORIGINAL_CONSOLE.warn;
      console.error = ORIGINAL_CONSOLE.error;
    }
  }

  enable() {
    if (!this.allowBrowserControl) {
      ORIGINAL_CONSOLE.warn('⚠️ Debug control disabled in production mode');
      return;
    }

    this.enabled = true;
    this.setupOverride();
    ORIGINAL_CONSOLE.info('✅ Debug logs enabled');
  }

  disable() {
    if (!this.allowBrowserControl) {
      ORIGINAL_CONSOLE.warn('⚠️ Debug control disabled in production mode');
      return;
    }

    this.enabled = false;
    this.setupOverride();
    ORIGINAL_CONSOLE.info('🔇 Debug logs disabled');
  }

  toggle() {
    if (!this.allowBrowserControl) {
      ORIGINAL_CONSOLE.warn('⚠️ Debug control disabled in production mode');
      return;
    }

    if (this.enabled) {
      this.disable();
    } else {
      this.enable();
    }
  }

  // Status check method
  status() {
    ORIGINAL_CONSOLE.info('🔍 Debug Control Status:', {
      enabled: this.enabled,
      production: IS_PRODUCTION,
      browserControl: this.allowBrowserControl,
      envDebugVar: process.env.REACT_APP_DEBUG
    });
  }
}

const debugControl = new DebugControl();

// Make available globally only in development
if (!IS_PRODUCTION && typeof window !== 'undefined') {
  window.debugControl = debugControl;

  // Add helpful methods to window for easy access
  window.enableLogs = () => debugControl.enable();
  window.disableLogs = () => debugControl.disable();
  window.toggleLogs = () => debugControl.toggle();
  window.logStatus = () => debugControl.status();
}

export default debugControl;